import { Log, type Profile, type SigninRequest, type User, UserManager, WebStorageStateStore } from 'oidc-client';
import { IDENTITY_CONFIG, METADATA_CONFIG } from '../helpers/auth-settings';
import { DEFAULT_IDP } from '../helpers/constants';

Log.logger = console;
Log.level = Log.DEBUG;

export default class AuthService {
  private static instance: AuthService | null = null;

  private UserManager: UserManager | undefined;

  /**
   * Creates — or returns — the singleton AuthService instance.
   *
   * **Singleton behaviour:** If an instance already exists, the constructor
   * returns it and the provided `args` are silently ignored. The
   * `UserManager` retains the configuration from the first instantiation.
   * Call {@link AuthService._resetInstance} to discard the current instance
   * before constructing a new one with different arguments.
   *
   * @param args - Configuration options (only honoured on the first call).
   * @param args.idp - Identity provider identifier. Defaults to {@link DEFAULT_IDP}.
   */
  constructor(args = { idp: DEFAULT_IDP }) {
    if (AuthService.instance) {
      return AuthService.instance;
    }

    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        ...METADATA_CONFIG,
      },
      extraQueryParams: { identity_provider: args.idp },
    });

    Log.logger = console;
    Log.level = Log.DEBUG;

    this.UserManager.events.addSilentRenewError((e) => {
      console.log('silent renew error', e.message);
    });
    this.UserManager.events.addAccessTokenExpired(() => {
      console.log('token expired');
      this.signinSilent();
    });

    AuthService.instance = this;
    return this;
  }

  signinRedirectCallback = (): Promise<User> => {
    return this.UserManager!.signinRedirectCallback();
  };

  getUser = async (): Promise<User> => {
    const user = await this.UserManager!.getUser();
    if (!user) {
      return await this.UserManager!.signinRedirectCallback();
    }
    return user;
  };

  signinRedirect = (idp?: string, redirectQueryParams?: unknown): Promise<void> => {
    if (redirectQueryParams) {
      localStorage.setItem('redirectQueryParams', JSON.stringify(redirectQueryParams));
    } else {
      // remove old query params if none added to signin request
      localStorage.removeItem('redirectQueryParams');
      localStorage.setItem('redirectPath', window.location.pathname);
    }

    // add extraQueryParams to signinRedirect, default is identity_provider myosh-default if any other remove identity_provider and set the domain as idp_identifier.
    let args: { extraQueryParams: { identity_provider?: string; idp_identifier?: string } } = {
      extraQueryParams: { identity_provider: DEFAULT_IDP },
    };

    if (idp !== DEFAULT_IDP) {
      args = { extraQueryParams: { idp_identifier: idp } };
    }

    return this.UserManager!.signinRedirect(args);
  };

  /**
   * Stores the user's subject identifier (sub claim) in localStorage.
   * @param profile - The user's OIDC profile containing the sub claim.
   */
  setUser = (profile: Pick<Profile, 'sub'>) => {
    return localStorage.setItem('userId', profile.sub);
  };

  signinSilent = async (): Promise<User> => {
    return this.UserManager!.signinSilent()
      .then((user) => {
        return user;
      })
      .catch((err) => {
        return err;
      });
  };

  signinSilentCallback = () => {
    return this.UserManager!.signinSilentCallback();
  };

  createSigninRequest = (): Promise<SigninRequest> => {
    return this.UserManager!.createSigninRequest();
  };

  logout = () => {
    return this.UserManager!.signoutRedirect({
      // Prevent the library from adding the standard param to avoid causing conflicts
      post_logout_redirect_uri: undefined,
      extraQueryParams: {
        client_id: IDENTITY_CONFIG.client_id,
        logout_uri: IDENTITY_CONFIG.post_logout_redirect_uri,
      },
    });
  };

  signoutRedirectCallback = () => {
    this.UserManager!.clearStaleState();
    return this.UserManager!.signoutRedirectCallback().then(() => {
      localStorage.clear(); // clear all potentially locally stored items
      window.location.replace('/login');
    });
  };

  /**
   * Set the instance to null.
   * This method is used to reset the singleton instance of AuthService.
   * It can be useful for testing purposes or when you want to create a new instance.
   * @private
   * @static
   * @returns {void}
   */
  static _resetInstance(): void {
    AuthService.instance = null;
  }
}
